import React, { FC } from 'react';

import { CheckCircleIcon, InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Tooltip } from '../../../../components/tooltip/index';

export type IssuanceRowStatusProps = {
  /** Whether the row satisfies its schema; drives the badge. */
  isComplete: boolean;
  isReverseVestingEnabled: boolean;
  /**
   * Whether the reverse-vesting block is on but not yet valid. Only this raises "Missing
   * information": the rest of the row is plainly visible in the open card, so flagging a fresh blank
   * row read as an error before the user had typed anything.
   */
  isReverseVestingIncomplete: boolean;
  stakeholderName: string;
};

/**
 * Collapsed-row header for the Share Issuance and Company Incorporation issuance rows: a
 * completeness badge, the stakeholder's name, and a status line that reports either the row's
 * reverse-vesting setting or that its reverse-vesting schedule is still missing information.
 */
export const IssuanceRowStatus: FC<IssuanceRowStatusProps> = ({
  isComplete,
  isReverseVestingEnabled,
  isReverseVestingIncomplete,
  stakeholderName,
}) => (
  <div className="flex min-w-0 items-center gap-3">
    {isComplete ? (
      <CheckCircleIcon className="size-4" iconColor="#12B76A" />
    ) : (
      <InfoCircle className="size-4" iconColor="#F79009" />
    )}
    <div className="flex min-w-0 flex-col justify-center">
      <Tooltip childrenWrapperClassName="max-w-[350px]" content={stakeholderName}>
        <span className="truncate text-sm font-[450] text-gray-700">{stakeholderName}</span>
      </Tooltip>
      {isReverseVestingIncomplete ? (
        <span className="text-xs font-[450] text-sunbeam-500">
          <AppFormattedMessage id="MISSING_INFORMATION" />
        </span>
      ) : (
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage
            id={isReverseVestingEnabled ? 'REVERSE_VESTING_ENABLED' : 'REVERSE_VESTING_DISABLED'}
          />
        </span>
      )}
    </div>
  </div>
);
